import { FC, ReactNode } from "react";

import { Box, Typography } from "@mui/material";

interface Props {
	dashboardHeader?: string | ReactNode | null;
	dashboardSubheader?: string | ReactNode;
	buttons?: ReactNode;
	children?: ReactNode;
	gridAutoRows?: string | Record<string, string>;
	fullViewport?: boolean;
}

const DashboardGrid: FC<Props> = ({
	children,
	dashboardHeader = "Dashboard",
	dashboardSubheader,
	buttons,
	gridAutoRows = "140px",
	fullViewport = false,
}) => {
	const showHeader = dashboardHeader != null || buttons != null;

	return (
		<Box
			sx={{
				padding: fullViewport
					? { xs: "0.75rem", sm: "1rem 1.5rem" }
					: { xs: "1rem", sm: "1.5rem 2rem" },
				height: fullViewport ? "100%" : undefined,
				minHeight: fullViewport ? 0 : "100%",
				display: fullViewport ? "flex" : "block",
				flexDirection: fullViewport ? "column" : undefined,
				flex: fullViewport ? 1 : undefined,
				minWidth: fullViewport ? 0 : undefined,
				width: "100%",
				overflow: fullViewport ? "hidden" : undefined,
				bgcolor: "background.default",
			}}
		>
			{showHeader && (
				<Box
					sx={{
						display: "flex",
						flexDirection: { xs: "column", md: "row" },
						justifyContent: { xs: "start", md: "space-between" },
						alignItems: { xs: "start", md: "center" },
						marginBottom: fullViewport ? "1rem" : "2rem",
						gap: { xs: "1rem", md: "0" },
						flexShrink: 0,
					}}
				>
					<Box>
						{typeof dashboardHeader === "string" ? (
							<>
								<Typography variant="h3" component="h1">
									{dashboardHeader}
								</Typography>
								{dashboardSubheader && (
									<Typography variant="h5" component="h2">
										{dashboardSubheader}
									</Typography>
								)}
							</>
						) : (
							dashboardHeader
						)}
					</Box>
					<Box sx={{ alignSelf: { xs: "flex-end", md: "auto" } }}>{buttons}</Box>
				</Box>
			)}
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(12, 1fr)",
					gridAutoRows: gridAutoRows,
					gap: fullViewport ? "1.25rem" : "2rem",
					rowGap: fullViewport ? "1.25rem" : { xs: "1rem", md: "3rem" },
					flex: fullViewport ? 1 : undefined,
					minHeight: fullViewport ? 0 : undefined,
					width: "100%",
					minWidth: 0,
					alignSelf: "stretch",
				}}
			>
				{children}
			</Box>
		</Box>
	);
};

export default DashboardGrid;
